import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, SlidersHorizontal, Pencil, Check, X, RotateCcw } from 'lucide-react';
import { ProvenanceBadge } from '@/components/trust/ProvenanceBadge';
import {
  buildAssumptionRows, summarizeAssumptions, ASSUMPTION_EDITORS, buildAssumptionPatch,
} from '@/lib/assumptions';

/** Inline editor for one assumption row. */
function AssumptionEditor({ id, initial, onApply, onCancel }) {
  const { t } = useTranslation();
  const editor = ASSUMPTION_EDITORS[id];
  const [value, setValue] = useState(initial ?? '');

  const commit = () => {
    const patch = buildAssumptionPatch(id, value === '' ? null : value);
    if (patch) onApply(patch);
    else onCancel();
  };

  return (
    <div className="flex items-center gap-1.5">
      {editor.type === 'select' ? (
        <select
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-8 rounded-[6px] border border-[#DDE1E5] bg-white px-2 text-sm text-[#171A1D] focus:border-[#78B928] outline-none"
          data-testid={`assumption-input-${id}`}
          aria-label={t('assumptions.editLabel', { label: t(`assumptions.labels.${id}`) })}
        >
          {editor.options.map(option => (
            <option key={option} value={option}>{t(`${editor.labelPrefix}.${option}`)}</option>
          ))}
        </select>
      ) : (
        <input
          autoFocus
          type="number"
          value={value}
          min={editor.min}
          max={editor.max}
          step={editor.step}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') onCancel();
          }}
          className="h-8 w-28 rounded-[6px] border border-[#DDE1E5] bg-white px-2 text-sm text-[#171A1D] focus:border-[#78B928] outline-none"
          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          data-testid={`assumption-input-${id}`}
          aria-label={t('assumptions.editLabel', { label: t(`assumptions.labels.${id}`) })}
        />
      )}
      <button
        type="button"
        onClick={commit}
        className="h-8 w-8 inline-flex items-center justify-center rounded-[6px] bg-[#527F19] text-white hover:bg-[#3F6114] transition-colors duration-150"
        data-testid={`assumption-apply-${id}`}
        aria-label={t('assumptions.save')}
        title={t('assumptions.save')}
      >
        <Check className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="h-8 w-8 inline-flex items-center justify-center rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:bg-[#F5F6F7] transition-colors duration-150"
        data-testid={`assumption-cancel-${id}`}
        aria-label={t('assumptions.cancel')}
        title={t('assumptions.cancel')}
      >
        <X className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Compact, expandable "Assumptions used" block. Makes an estimate challengeable.
 * With `onEdit` it also becomes the inline assumption editor: every edit is a
 * config change, so the authoritative engine recalculates the whole estimate.
 */
export function AssumptionsPanel({ assumptions, formatCost, onReview, onEdit, config, defaultOpen = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(null);

  if (!assumptions || assumptions.length === 0) return null;

  const { defaults, userInputs } = summarizeAssumptions(assumptions);
  const rows = buildAssumptionRows(assumptions, { t, formatCost });
  const editable = (id) => Boolean(onEdit) && Boolean(ASSUMPTION_EDITORS[id]);

  const initialValue = (row) => {
    const editor = ASSUMPTION_EDITORS[row.id];
    const raw = config?.[editor.field];
    if (editor.type === 'select') return raw ?? 'hybrid';
    const assumption = assumptions.find(a => a.id === row.id);
    return raw ?? assumption?.value ?? '';
  };

  const apply = (patch) => {
    setEditing(null);
    onEdit(patch);
  };

  return (
    <section className="border border-[#DDE1E5] rounded-lg bg-white" data-testid="assumptions-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-[#F5F6F7] transition-colors rounded-lg"
        data-testid="assumptions-toggle"
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-[#171A1D]">{t('assumptions.title')}</span>
          <span className="block text-xs text-[#66707A] mt-0.5" data-testid="assumptions-summary">
            {t('assumptions.summary', { defaults, user: userInputs })}
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 text-[#66707A] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="px-5 pb-4 border-t border-[#DDE1E5]" data-testid="assumptions-body">
          <p className="text-xs text-[#66707A] pt-3 pb-1">
            {onEdit ? t('assumptions.editHint') : t('assumptions.intro')}
          </p>
          <dl x-excluded="true" className="divide-y divide-[#DDE1E5]">
            {rows.map(row => (
              <div
                key={row.id}
                className="py-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1"
                data-testid={`assumption-${row.id}`}
              >
                <dt className="text-sm text-[#3A4249] min-w-0 break-words">{row.label}</dt>
                <dd className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 min-w-0">
                  {editing === row.id ? (
                    <AssumptionEditor
                      id={row.id}
                      initial={initialValue(row)}
                      onApply={apply}
                      onCancel={() => setEditing(null)}
                    />
                  ) : (
                    <>
                      <span className="text-sm text-[#171A1D] break-words" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {row.value}
                      </span>
                      <ProvenanceBadge provenance={row.provenance} />
                      {editable(row.id) && (
                        <button
                          type="button"
                          onClick={() => setEditing(row.id)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7] transition-colors duration-150"
                          data-testid={`assumption-edit-${row.id}`}
                          aria-label={t('assumptions.editLabel', { label: row.label })}
                          title={t('assumptions.edit')}
                        >
                          <Pencil className="w-3 h-3" aria-hidden="true" />
                        </button>
                      )}
                      {editable(row.id) && ASSUMPTION_EDITORS[row.id].resettable
                        && config?.[ASSUMPTION_EDITORS[row.id].field] != null && (
                        <button
                          type="button"
                          onClick={() => apply(buildAssumptionPatch(row.id, null))}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-[6px] border border-[#DDE1E5] text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7] transition-colors duration-150"
                          data-testid={`assumption-reset-${row.id}`}
                          aria-label={t('assumptions.resetDefault')}
                          title={t('assumptions.resetDefault')}
                        >
                          <RotateCcw className="w-3 h-3" aria-hidden="true" />
                        </button>
                      )}
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          {onEdit && (
            <p className="mt-3 text-xs text-[#8A929B]" data-testid="assumptions-override-note">
              {t('assumptions.overrideNote')}
            </p>
          )}
          {onReview && (
            <button
              type="button"
              onClick={onReview}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#527F19] hover:text-[#3F6114] transition-colors"
              data-testid="assumptions-review-btn"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden="true" />
              {t('assumptions.review')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
